/**
 * メインレンダラ — `AppState` から 1 フレームを描画する副作用関数。
 *
 * spec §3.3 の二相レンダリングモデルに従い、`state.progress` の厳密判定で
 * `renderPreExplosion` / `renderExplosion` に dispatch する。Phase C 以降は
 * 外周テキストレイヤーが両 phase 共通で最後に描画される（spec §5.4.3）。
 *
 * Phase D（Breath Motion）で本モジュールは module-level の状態を 2 つ持つ:
 *
 * 1. `clock: Clock` — idle 呼吸の時刻源（`performance.now()` 基準）。
 *    progress=0 の間だけ進行し、DETONATE で pause、Reset で reset。Breath の
 *    時間軸 `t` はこの Clock から供給される。
 * 2. `prevProgress: number` — 直前フレームの progress。0→>0 / >0→0 の
 *    遷移エッジを検出して Clock の状態遷移（pause / reset）を起こす。
 *
 * これらは「単一キャンバスを連続再生する」前提で動く。複数 `render()` を
 * 並列に回す要件が出た場合はクロージャ化 or class 化する必要あり（当面 v1.1
 * 範囲では不要と判断、spec §3.3 / Phase D 仕様）。
 *
 * 2026-04-20 bug 修正:
 * 初版 Phase D 実装は `frozenSilhouette` で progress>0 中の silhouette を
 * 1 回だけキャプチャして使い回す設計だったが、これは Phase C の核心機能
 * （毎フレーム `warpPolygonByCells(..., state.progress)` で silhouette を
 * 破片群に追従させる膨張）を破壊し、破片が silhouette clip に打ち切られて
 * 飛び散らない bug を招いた。正しくは Breath の時間変動のみ凍結すればよく、
 * warp(progress) は `buildSilhouette` に毎フレーム任せる。Breath の skip は
 * `buildSilhouette` 内で `if (t !== undefined && state.progress === 0)`
 * により自動実施されるため、renderer 側の凍結ロジックは冗長。
 *
 * パイプライン（Phase F Step 4 — 3 スロット overlay 対応、5 層モデル）:
 * 1. `computeLayout(state)` で `Layout` を導出
 * 2. `state.sceneCache` を scene として利用（null のときは `buildScene`
 *    フォールバックで初期化前の描画要求にも応える）
 * 3. **Background 層**: `backgroundTransparent` なら `clearRect`、そうでなければ
 *    `backgroundColor` で `fillRect`。続いて `overlays.background` がある場合は
 *    **cover fit** で canvas 全面に drawImage（以降の Inner / Surface の地）
 * 4. **silhouette を 1 回だけ計算**し、phase / text 間で共有する
 *    （OQ-14 / 60fps 保守、D-16）
 * 5. **Inner 層**: silhouette 内側を `innerColor` でベタ塗り →
 *    `overlays.inner` がある場合は silhouette clip 下で **cover fit** drawImage
 *    （idle = 現在の silhouette / explosion = DETONATE 時点の silhouette で
 *    clip する。画像自体は静止画で、clip 形状だけが時間変動する）
 * 6. **Surface 層**: per-phase で `renderPreExplosion` / `renderExplosion` に
 *    dispatch。composite (surface 色 + `overlays.surface`) を silhouette 内 /
 *    破片群内で drawImage する
 * 7. **Text 層**: silhouette を offset → rotate → path 化し `drawTextAlongPath`
 *    で外周描画（spec §5.4）
 *
 * 呼び出し側の責務:
 * - `ctx.canvas.width/height` を `Layout.outerW/H` に同期させる（main.ts
 *   の `rebuildScene` / resize 時に stage が行う）
 * - 毎フレーム scheduleRender 経由で `render(ctx, state)` を呼ぶ
 * - 呼吸が必要か（progress=0 かつ breath amp > 0）を `shouldContinueAnimation`
 *   で問い合わせ、次フレームの rAF を需要駆動で要求する（Phase D 最適化）。
 *
 * 本関数はキャンバスへの描画 + module-level の clock/frozen 更新に副作用を
 * 限定する。ストア書き戻し / rAF ループ制御は main.ts の責務。
 *
 * 設計参照:
 * - docs/happa-designtool-v1.1-spec.md §3.3 / §5.4 / §8 / Phase D / Phase F Step 4
 * - wireframes/v0.10/wireframe.html L853–893
 */

import { Clock } from '../core/clock';
import { computeInnerOutline } from '../core/innerOutline';
import { offsetPolygonCurve } from '../core/offset';
// `offsetPolygonRadial` は Phase C.1 Step 2 で renderer.ts 本体の依存から
// 消え、Phase K1 で scene.ts の shrunkPoly 生成も撤去されたため、現在は
// `polygon.ts` が提供するだけの public API になっている（将来の再利用余地）。
import { buildSilhouette, buildTextPath } from '../core/silhouette';
import type { PlacementOptions } from '../core/textLayout';
import { buildTextBasePath, relaxMinRadius, rotateToTopRayAnchor } from '../core/textPath';
import type { Vec2 } from '../core/types';
import type { AppState } from '../state';
import type { OverlayImage } from '../state/types';
import { buildComposite, buildMasterComposite } from './composite';
import { computeCoverRect, computeLayout } from './layout';
import { renderExplosion } from './phases/explosion';
import { renderPreExplosion } from './phases/preExplosion';
import { buildScene } from './scene';
import { drawTextAlongPath } from './text';

// ── module-level state（Phase D Step 3） ─────────────────────────────────
// 単一 canvas 前提。複数インスタンス要件が出たらクロージャ化で state を束ねる。

/** idle 呼吸の時刻源。progress=0 中のみ進み、detonate 中は pause される。 */
const clock = new Clock();

/**
 * 直前フレームの `state.progress`。0→>0 / >0→0 のエッジ検出に使う。
 * 初期値 0 = 初回 render 前は idle 相とみなす（spec §3.3 と整合）。
 */
let prevProgress = 0;

/**
 * DETONATE 突入時に焼いた master composite（surface + Surface overlay を
 * silhouette の bbox に letterbox fit した offscreen canvas）を保持する
 * （Phase E Step 4 bug 修正、2026-04-21）。
 *
 * この frozen master は破片描画で「割れる前の 1 枚絵」として全破片が参照する。
 * 各破片は元座標の `cell.points` で clip → `drawImage(frozenMaster, 0, 0)` の
 * 形で描画され、破片が動いても master は画面原点に貼り付いたまま、結果として
 * 「破片の中に元位置の画像が映る」紙が破れる表現になる。
 *
 * 生存期間:
 * - DETONATE トリガー（prevProgress=0 → progress>0）で構築
 * - Reset（progress>0 → 0）で null に戻す
 * - `overlays.surface` が null のときは構築しない（null のまま、破片描画は
 *   solid fill）
 */
let frozenMasterComposite: HTMLCanvasElement | null = null;

/**
 * 現在の `state` から 1 フレームを描画する（spec §3.3 二相モデル + Phase D 呼吸
 * + Phase F Step 4 3 スロット overlay）。
 *
 * `state.sceneCache === null` のフォールバックで `buildScene(L, state)` を
 * 直接呼ぶのは、main.ts の初期化順序によっては sceneCache が set される前に
 * resize → render が走る可能性があるため（ストアに書き戻すと無限ループに
 * なりかねないので呼ばない。その場でローカル Scene を作って描画する）。
 *
 * `progress === 0` 判定は厳密比較（`>` 境界の浮動小数誤差を避けるため
 * 0 を明示的に pre-phase へ送る）。rAF ループで `progress` が 1e-9 でも
 * 動き始めると explosion 相に移る。
 *
 * silhouette 共有（OQ-14）: subdivide + warp + Breath を `buildSilhouette`
 * に集約し、本関数で **1 回だけ**呼んで両 phase + text レイヤーに同じ結果を
 * 渡す。破片数 240 でも余計な再計算コストを払わない。
 *
 * Phase D 追加の状態遷移ロジック:
 * - `prevProgress === 0 && state.progress > 0` → **DETONATE 突入**:
 *   clock を pause（呼吸の t を凍結）。以降の progress>0 フレームでは
 *   `buildSilhouette` が Breath を skip するため、silhouette は
 *   `warpPolygonByCells(subdivided, realCells, state.progress)` のみに基づき、
 *   Phase C と同じく progress 依存で膨張する。
 * - `prevProgress > 0 && state.progress === 0` → **Reset**:
 *   clock.reset() で t=0 から呼吸を再開する（仕様書「Reset で clock t=0 に
 *   リセット」に従う。resume ではなく reset）。
 *
 * @param ctx   描画先の 2D コンテキスト。
 * @param state アプリケーション状態のスナップショット。
 */
export function render(ctx: CanvasRenderingContext2D, state: AppState): void {
  const L = computeLayout(state);
  const scene = state.sceneCache ?? buildScene(L, state);

  const W = L.outerW;
  const H = L.outerH;

  // ── 1. Background 層（最下層） ──────────────────────────────────
  // 旧実装は Inner 色を canvas 全体に敷いていたが、これは Inner 層の責務を
  // 侵していた（silhouette の外にも Inner 色が出る）。正しい層モデルは
  // Background（地）→ Inner（silhouette のみ）→ Surface（破片）→ Text。
  // transparent 時は fillStyle を使わず clearRect のみ（PNG 出力で背景を
  // 抜くための合意）。
  // Phase F Step 4: 背景色塗り後に `overlays.background` を cover fit で
  // 全面に重ねる。cover は CSS `object-fit: cover` と同じく矩形を完全に覆い、
  // 余剰をクロップする（letterbox 余白を作らない）。
  if (state.backgroundTransparent) {
    ctx.clearRect(0, 0, W, H);
  } else {
    ctx.fillStyle = state.backgroundColor;
    ctx.fillRect(0, 0, W, H);
  }
  drawOverlayCover(ctx, state.overlays.background, 0, 0, W, H);

  // ── 2. silhouette の決定（Phase D bug 修正後） ─────────────────
  // 毎フレーム buildSilhouette を呼ぶ。内部で:
  // - warp(progress) は常に再計算（Phase C の破片追従機能を維持）
  // - Breath は progress===0 かつ t!==undefined のときのみ加算
  //   → Clock.pause() 中は t が進まないので Breath フェーズは DETONATE 突入
  //     時点で固定される（結果として Breath の時間変動は破壊中に波及しない）
  const silhouette = buildSilhouette(state, scene, clock.t);

  // ── 3. Phase 遷移エッジ検出（prevProgress vs 現フレーム state.progress） ──
  // 「0→>0」と「>0→0」以外（idle 継続 / detonate 継続）では Clock にも
  // frozenMasterComposite にも触らない。
  // 2 重 pause / 2 重 reset は Clock 側が no-op 保証している（clock.ts 参照）。
  if (prevProgress === 0 && state.progress > 0) {
    clock.pause();
    // Phase E Step 4 bug 修正: DETONATE 突入時に master composite を焼く。
    // `overlays.surface` が null なら焼かない（frozenMaster も null のまま、
    // 破片は solid surface fill で飛散する既存挙動）。silhouette は本フレーム
    // の warp 済みのもの（progress は極小 > 0 なので Breath は適用されない）。
    if (state.overlays.surface !== null) {
      frozenMasterComposite = buildMasterComposite({
        outerW: W,
        outerH: H,
        surfaceColor: state.surfaceColor,
        overlay: state.overlays.surface,
        silhouettePolygon: silhouette,
      });
    }
  } else if (prevProgress > 0 && state.progress === 0) {
    clock.reset();
    // Reset 時は frozen composite を破棄。次の idle フレームからは per-frame
    // buildComposite / 現在 silhouette に戻る（breath で silhouette が動くため
    // 毎フレーム再構築が必要）。
    frozenMasterComposite = null;
  }

  // Inner 層の clip 形状（Phase G Option A）:
  // 現フレームの warped silhouette をそのまま Inner clip に使う。warped
  // silhouette は `warpPolygonByCells` で破片群の envelope として膨張する
  // よう設計されているため、破片の隙間を含む「広く繋がった面」が得られる。
  // 旧実装の破片 union 方式（disjoint polygon に分裂して Inner が見えない）
  // は 2026-04-21 に撤回。
  const innerClipPolys: Vec2[][] = computeInnerClipPolys(state.progress, silhouette);

  // ── 4. Inner 層（innerColor + overlays.inner、各 clip polygon に対して描画）──
  drawInnerLayer(ctx, innerClipPolys, W, H, state.innerColor, state.overlays.inner);

  // ── 5. Surface 層の composite 決定 ─────────────────────────────
  // progress=0（idle）: per-frame buildComposite を使う（Pulse で silhouette
  //   が微変化するため、毎フレーム bbox を更新して PNG を cover fit し直す
  //   必要がある）。Phase H Step 3: cover fit + bbox 追従で PNG が silhouette
  //   と 1:1 で追従する。Pulse は一様スケールで AABB も比例して拡縮するため
  //   別途 transform を張る必要はない（Rotation は Phase H Step 1 で削除済み）。
  // progress>0（explosion）: DETONATE 時に焼いた frozenMasterComposite を使う。
  //   これにより破片は「割れる前の 1 枚絵」を共通参照し、各破片の per-cell
  //   transform と組み合わせて「元位置の画像が破片の中に映る」効果が得られる。
  //   `overlays.surface` が null なら composite も null（solid fill fallback）。
  let composite: HTMLCanvasElement | null;
  if (state.progress === 0) {
    composite =
      state.overlays.surface !== null
        ? buildComposite({
            outerW: W,
            outerH: H,
            surfaceColor: state.surfaceColor,
            overlay: state.overlays.surface,
            silhouettePolygon: silhouette,
          })
        : null;
  } else {
    composite = frozenMasterComposite;
  }

  // ── 6. Surface 層（破片 / 無傷 silhouette）───────────────────────
  if (state.progress === 0) {
    renderPreExplosion(ctx, L, scene, state, silhouette, composite);
  } else {
    renderExplosion(ctx, L, scene, state, silhouette, composite);
  }

  // ── 7. Text 層 ──────────────────────────────────────────────────
  drawTextLayer(ctx, state, silhouette, L.textMargin, L.fontSize);

  // ── 8. Foreground 層（Phase J D）─────────────────────────────────
  // 最前面。Background 同様に canvas 全面を cover fit で覆う。破片も text も
  // 上から隠す形になるので、ロゴマーク / テクスチャ / ウォーターマークに使う。
  // clip は張らず canvas 全面を直接 cover fit する（Background と対称）。
  drawOverlayCover(ctx, state.overlays.foreground, 0, 0, W, H);

  // 次フレームの遷移判定用に progress をスナップ。fallthrough で end-of-frame に
  // 必ず更新する（途中 early-return は導入しないこと）。
  prevProgress = state.progress;
}

/**
 * 次フレームの rAF を要求すべきかを判定する（Phase D 需要駆動アニメーション）。
 *
 * main.ts の rAF ループがこの関数を参照して「呼吸アニメが動くべきか」を
 * 判断する。本関数自体は副作用なしの純粋判定（clock には触れない）。
 *
 * true を返す条件:
 * - `progress === 0` **かつ** breath のいずれかの amp が > 0
 *   （= 呼吸が実際に silhouette を動かしている）
 *
 * false となるケース:
 * - `progress > 0`: detonate は main.ts 側の rAF ループ（startDetonation）が
 *   既に別系統で回っているため、こちらは需要を主張しない。
 * - 全 amp = 0: Breath 無効化 → silhouette は静止 → 追加フレーム不要。
 *
 * 注: main.ts 側は既存の `scheduleRender` を据え置きつつ、idle 中に本関数を
 * 参照する小さな rAF ループを足せばよい（@render-agent が main.ts を触れない
 * ため、配線は @ui-agent または main owner に別途依頼する）。
 *
 * @param state 現在の AppState スナップショット。
 * @returns 次の rAF を要求すべきなら true。
 */
export function shouldContinueAnimation(state: AppState): boolean {
  if (state.progress !== 0) return false;
  return state.breath.pulseAmp > 0;
}

/**
 * polygon の頂点列を ctx の現在 path へ積む（moveTo / lineTo / closePath）。
 *
 * 複雑度を抑えるため本モジュールと phase 両方で同じ形式の helper を持つ。
 * 頂点 < 3 や先頭が undefined の退化入力では何もせず抜ける（ctx.beginPath は
 * 呼ばない、呼び出し側の起点管理に任せる）。
 */
function tracePolyOnCtx(ctx: CanvasRenderingContext2D, poly: Vec2[]): void {
  if (poly.length < 3) return;
  const first = poly[0];
  if (!first) return;
  ctx.moveTo(first[0], first[1]);
  for (let i = 1; i < poly.length; i++) {
    const p = poly[i];
    if (!p) continue;
    ctx.lineTo(p[0], p[1]);
  }
  ctx.closePath();
}

/**
 * 指定の destination 矩形に `overlay` を cover fit で貼る（Phase F Step 4）。
 *
 * - overlay が null / 未ロード / 画像サイズ 0 の場合は no-op（呼び出し側の
 *   ctx 状態は一切変えない）。
 * - 呼び出し側は save/restore や clip の前後関係を自分で管理すること。
 *   本関数内では drawImage しか呼ばず、globalAlpha 等も変更しない。
 * - `destW` / `destH` が 0 のケースも no-op（ゼロ除算回避）。
 *
 * @param ctx     描画先の 2D コンテキスト。
 * @param overlay Overlay wrapper。null or 未ロードなら描画しない。
 * @param destX   描画先 X（canvas 座標）。
 * @param destY   描画先 Y（canvas 座標）。
 * @param destW   描画先幅。
 * @param destH   描画先高。
 */
function drawOverlayCover(
  ctx: CanvasRenderingContext2D,
  overlay: OverlayImage | null,
  destX: number,
  destY: number,
  destW: number,
  destH: number,
): void {
  if (overlay === null) return;
  if (destW <= 0 || destH <= 0) return;
  const img = overlay.image;
  if (!img.complete) return;
  if (img.naturalWidth <= 0 || img.naturalHeight <= 0) return;

  const { sx, sy, sw, sh } = computeCoverRect(img.naturalWidth, img.naturalHeight, destW, destH);
  // imageSmoothingQuality は縮小時の jaggy 抑止。main canvas 全体の
  // smoothing を汚染するが、以降の Surface 描画（drawImage / fill）は
  // いずれもドット絵的な要求がないため実害なし。
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, sw, sh, destX, destY, destW, destH);
}

/**
 * Inner 層の clip 形状配列を決定する（Phase G Option A、2026-04-21 確定版）。
 *
 * Idle / Explosion どちらも現フレームの warped silhouette を 1 要素配列で
 * 返す。warped silhouette は `warpPolygonByCells` で破片群の envelope として
 * 膨張するよう設計されており、そのまま Inner clip として使うと「破片の隙間
 * を含む広く繋がった面」が Inner 色 / Inner PNG として露出する。
 *
 * 旧 Phase G 初版は `computeInnerOutline` に fragments を渡して
 * polygon-clipping の Boolean union を計算していたが、破片が disjoint に
 * なる progress 中盤以降で Inner が各破片真下だけにしか描画されない症状
 * （破片で覆われて視認できない）を招き、撤回（2026-04-21）。詳細は
 * `src/core/innerOutline.ts` の header 参照。
 *
 * 本関数は退化 silhouette（< 3 頂点）の吸収を `computeInnerOutline` に
 * 委譲するだけの薄いラッパー。将来 outline を複数化する拡張が入ったとき
 * に呼び出し側の責務を `computeInnerOutline` に集約しやすくするため、
 * 型契約の形は保ったまま残す。
 */
function computeInnerClipPolys(progress: number, silhouette: Vec2[]): Vec2[][] {
  return computeInnerOutline(silhouette, progress);
}

/**
 * Inner 層（innerColor ベタ塗り + `overlays.inner` cover）を描画する（Phase G）。
 *
 * 旧 Phase F 実装は silhouette 単一 polygon で clip し、Inner overlay を
 * silhouette bbox に cover fit していた。Phase G では以下に変更:
 *
 * 1. clip 形状は複数 polygon を受け取る（破片 union の disjoint polygon 群）。
 *    各 polygon ごとに独立に save/clip/fill/drawImage/restore を繰り返す。
 * 2. Inner overlay の cover fit 基準を **canvas 全面 (W × H)** に変更。
 *    これにより Inner PNG は「canvas 固定の 1 枚絵」として振る舞い、破片が
 *    動いて隙間が空いても、各 clip 領域からは「その座標にある PNG の
 *    該当部分」が見える（= 破片が PNG の上をスキャンするのではなく、
 *    破片の裏側に canvas 固定の絵があるように見える、仕様書の背景挙動）。
 *
 * 処理:
 * 1. `clipPolys.length === 0` なら no-op（Inner 描画 skip、破片 union が空）
 * 2. canvas 全面を target とする Inner overlay の cover rect を **1 回だけ**計算
 *    （各 clip スコープで再計算する必要はない、同じ結果のため）
 * 3. 各 polygon について:
 *    - save → clip polygon → innerColor で全面 fillRect → Inner overlay drawImage
 *    - restore で clip/描画状態を巻き戻す
 *
 * 各 polygon 描画で `fillRect(0, 0, W, H)` / `drawImage(..., 0, 0, W, H)` を
 * 呼んでいるが、clip で polygon 外はマスクされるので視覚的には polygon 内のみ
 * 塗られる。bbox を計算しないのは (a) 破片 union polygon の bbox 計算コストを
 * 削減、(b) Inner overlay を canvas 全面 cover に揃える設計意図のため。
 *
 * 下流の Surface 層は別の save/restore スコープで描画されるため、ここで
 * 張った clip は restore で解除され、Surface 側の clip ロジック（silhouette
 * または破片単位）と干渉しない。
 *
 * @param ctx           描画先 2D コンテキスト。
 * @param clipPolys     Inner 層の clip 形状配列（idle=[silhouette] /
 *                      explosion=破片 union の disjoint polygon 群）。
 * @param canvasW       canvas の outer 幅（Inner overlay cover 計算の target）。
 * @param canvasH       canvas の outer 高。
 * @param innerColor    Inner 色（HEX 文字列）。
 * @param innerOverlay  `overlays.inner`。null なら画像レイヤーは描かず色塗りのみ。
 */
function drawInnerLayer(
  ctx: CanvasRenderingContext2D,
  clipPolys: Vec2[][],
  canvasW: number,
  canvasH: number,
  innerColor: string,
  innerOverlay: OverlayImage | null,
): void {
  if (clipPolys.length === 0) return;
  if (canvasW <= 0 || canvasH <= 0) return;

  // Inner overlay の cover rect は canvas 全面を target として 1 度だけ計算。
  // 各 clip polygon で同じ rect を再利用する（清書時に drawOverlayCover を
  // 呼ぶと同じ計算を polygon 数ぶん繰り返すので、ここで前計算してループ外に
  // 出しておく）。overlay が null/未ロードなら `overlayReady === false` で
  // drawImage を skip。
  const overlayReady =
    innerOverlay?.image.complete === true &&
    innerOverlay.image.naturalWidth > 0 &&
    innerOverlay.image.naturalHeight > 0;
  const coverRect = overlayReady
    ? computeCoverRect(
        innerOverlay.image.naturalWidth,
        innerOverlay.image.naturalHeight,
        canvasW,
        canvasH,
      )
    : null;

  for (const poly of clipPolys) {
    if (poly.length < 3) continue;

    ctx.save();
    ctx.beginPath();
    tracePolyOnCtx(ctx, poly);
    ctx.clip();

    // Inner 色は canvas 全面に fill（clip で polygon 外はマスクされる）。
    ctx.fillStyle = innerColor;
    ctx.fillRect(0, 0, canvasW, canvasH);

    // Inner overlay は canvas 全面を target として cover fit で drawImage。
    // coverRect は null のときだけ skip（overlay なし or 未ロード）。
    if (overlayReady && coverRect !== null) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(
        innerOverlay.image,
        coverRect.sx,
        coverRect.sy,
        coverRect.sw,
        coverRect.sh,
        0,
        0,
        canvasW,
        canvasH,
      );
    }

    ctx.restore();
  }
}

/**
 * 外周テキストを silhouette に沿って描画する（spec §5.4）。
 *
 * `renderer.ts` 本体の複雑度（Biome 20 上限）を抑えるため分離した
 * private helper。両 phase 共通で silhouette 計算の結果を使い回す
 * （テキストは progress=0 でも 1 でも同じパイプラインで描ける）。
 *
 * 描画手順（spec §5.4）:
 * 1. text が空 / `textEnabled=false` なら何もせず return
 * 2. silhouette を `textMargin` ぶん外側へ offset してテキスト帯のハルを作る
 * 3. 12 時位置から始まるよう `rotateToTopRayAnchor` で回転（交点アンカー）
 * 4. `buildTextPath` で tangent 付き path に変換
 * 5. `drawTextAlongPath` に渡して 1 周ぶん描画
 *
 * silhouette 頂点が 3 未満 / path サンプル点が空の退化ケースでは
 * 描画をスキップ（下流の `drawTextAlongPath` 側にも防御ガードあり）。
 */
/**
 * 直近フレームで採用したテキストのリピート数（reps ヒステリシス用）。
 * pulse の周長振動で reps が閾値付近を flapping しないよう、
 * `computeGlyphPlacements` へ `preferredReps` として渡す。
 * 0 = 未確定（初回 / 中央 1 周ブランチ後）。
 */
let lastTextReps = 0;

/**
 * オーバーシュート演出の spacing 圧縮率（2026-07-10）。
 * envelope=1 のとき spacing は 1 − 0.9 = 10% まで詰まり、経路終端に
 * 生まれた空きへ追加テキストが seam から流れ込む。
 */
const TEXT_OVERSHOOT_SQUEEZE = 0.9;

/** smoothstep（0..1 clamp + 3t²−2t³）。envelope の立ち上がり/立ち下がり用。 */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * detonate 進行度 → テキストオーバーシュートの強度（0..1）。
 *
 * 爆発初期（perimeter が easeOutExpo で急拡大する p 0.02–0.18）に
 * 立ち上がり、FIX へ向かう p 0.50–0.95 で滑らかに 0 へ戻る。
 * p=0（idle / pulse）と p=1（FIX）では厳密に 0 —— 従来の
 * ジャスト詰めと完全同一の見た目を保証する。
 *
 * 演出意図: 膨張に合わせて多めのテキストが seam から溢れ出し、
 * 形が定まるときに余分が seam へ引っ込んでいく「映像的な」時間設計。
 */
function overshootEnvelope(p: number): number {
  if (p <= 0 || p >= 1) return 0;
  // 立ち上がり 0.02–0.10: perimeter の easeOutExpo 急拡大（+23% ≈ p 0.12）
  // より先に squeeze を深くし、reps 昇格の解禁（env ≥ 0.7）を間に合わせる。
  return smoothstep(0.02, 0.1, p) * (1 - smoothstep(0.5, 0.95, p));
}

/**
 * reps 昇格を許可する envelope 閾値。squeeze がこれより浅い区間で昇格すると
 * ジャスティファイ再配分がリング全体に見えてポップするため、深い squeeze
 * （再配分が不可視）に達するまで `holdReps` で保留する。
 */
const REPS_UNLOCK_ENV = 0.9;

function drawTextLayer(
  ctx: CanvasRenderingContext2D,
  state: AppState,
  silhouette: Vec2[],
  textMargin: number,
  fontSize: number,
): void {
  if (!state.textEnabled) return;
  if (state.text.trim() === '') return;
  if (silhouette.length < 3) return;

  // Phase C.1 Step 3: text 配置専用の滑らかな閉曲線を silhouette とは独立に
  // 生成する（凸包 ↔ silhouette 全頂点を `followSilhouette` で連続補間 →
  // 凹部深さ clamp → centripetal Catmull-Rom で spline 補間）。これにより
  // silhouette の凹部で text path が自己交差（8 の字）する問題を解消する。
  // smoothing は `buildTextBasePath` が内包するため、旧 `roundSharpCorners`
  // 呼び出し（Step 1 導入、Step 3 で撤去）は不要。
  // 第 4 引数 fontSize: グリフ寸法未満のジグザグ（detonate 中の warp 由来）を
  // 経路から落とすフロア（textPath.ts FEATURE_FLOOR_COEFF 参照）。文字が
  // 物理的に追従できない曲率を除去し、文字被りの根本原因の一つを断つ。
  const basePoly = buildTextBasePath(silhouette, state.followSilhouette, undefined, fontSize);
  const rawTextPoly = offsetPolygonCurve(basePoly, textMargin);
  // 最小回転半径 = fontSize を強制（曲率半径がグリフ寸法を下回るヘアピン
  // では文字が内側へ倒れ込んで密着する）。オフセットは凹側の半径を
  // textMargin ぶん縮めるため、オフセット後の最終ポリゴンに掛ける。
  const relaxedTextPoly = relaxMinRadius(rawTextPoly, fontSize);
  // 起点は「重心から真上の半直線と経路の交点」（2026-07-10 平滑化 F1）。
  // 旧 `rotateHullWithCorners`（最上端頂点）は pulse / detonate の連続変形で
  // 頂点が入れ替わった瞬間に pos=0 が離散ジャンプし、リング全体のテキストが
  // スライドして見えた。交点アンカーは変形に対して連続。
  const rotated = rotateToTopRayAnchor(relaxedTextPoly);
  const pathData = buildTextPath(rotated, []);
  if (pathData.points.length === 0) return;

  // reps ヒステリシス（平滑化 F3）: 前フレームの採用 reps を渡して
  // 周長振動による「1 周分のテキストの出現/消滅 flapping」を防ぐ。
  const layoutOptions: PlacementOptions = {};
  if (lastTextReps >= 1) layoutOptions.preferredReps = lastTextReps;
  // オーバーシュート演出: 爆発中だけ spacing を詰め、追加周回分を
  // クリップ許容で流し込む（overshootEnvelope の JSDoc 参照）。
  // extraReps=2 の根拠: 試行バッファが「入りうる量」を常に上回っていれば、
  // 見える文字数は seam のクリップ境界だけで決まり（連続）、reps 昇格の
  // 瞬間に 1 周分がリング全体へ一括出現するポップが起きない。squeeze で
  // 生まれる空き（≤1 周分）+ ヒステリシスの遅延分（≤1.15 周）を覆う。
  const env = overshootEnvelope(state.progress);
  if (env > 0) {
    layoutOptions.spacingScale = 1 - TEXT_OVERSHOOT_SQUEEZE * env;
    layoutOptions.extraReps = 2;
    // squeeze が浅い間は reps 昇格を保留（REPS_UNLOCK_ENV の JSDoc 参照）。
    if (env < REPS_UNLOCK_ENV) layoutOptions.holdReps = true;
  }
  lastTextReps = drawTextAlongPath(
    ctx,
    pathData,
    state.text,
    {
      fontSize,
      tracking: state.tracking,
      latinFontFamily: state.latinFont,
      jpFontFamily: state.japaneseFont,
      color: state.textColor,
    },
    layoutOptions,
  );
}
